import { useEffect, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import {
  useAddEvChargerMutation,
  useGetEvChargerCatalogQuery,
  useUpdateEvChargerMutation,
} from "../../../../../../redux/api/ev-charger/evChargerAPI";
import abbLogo from "../../../../../../shared/oversight-core/assets/images/abb.svg";
import citaLogo from "../../../../../../shared/oversight-core/assets/images/cita.svg";
import teslaLogo from "../../../../../../shared/oversight-core/assets/images/tesla.svg";
import AddEvChargerRequestDTO from "../../../../../../shared/oversight-core/dtos/request-dtos/add-ev-charger-request-dto";
import UpdateEVChargerRequestDTO from "../../../../../../shared/oversight-core/dtos/request-dtos/update-ev-charger-request-dto";
import { IPowerConsumerView } from "../../../../../../shared/oversight-core/interfaces/entities/power-consumer";
import { EVChargerCatalogItem } from "../../../../../../shared/oversight-core/interfaces/ev-charger-catalog-item";
import { IHttpError } from "../../../../../../shared/oversight-core/interfaces/http-errror";
import BackArrowButton from "../../../../../../shared/oversight-core/ui-elements/back-arrow-button/back-arrow-button";
import AppInput from "../../../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import { noSpecialCharsNoWhitespaceStartEndAndNotAllowCommaRegex } from "../../../../../../shared/oversight-core/utils/regex";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../../shared/oversight-core/utils/toast";
import styles from "./add-update-device-modal.module.scss";

interface IFormInput {
  name: string;
  brand: string;
  modelNumber: string;
  serialKey: string | null;
}

interface IBrandOption {
  id: string;
  label: string;
  logo?: string;
}

const defaultFormValues: IFormInput = {
  name: "",
  brand: "",
  modelNumber: "",
  serialKey: "",
};

interface IProps extends ModelContainerProps {
  show: boolean;
  deviceToEdit?: IPowerConsumerView | null;
  onSuccess?: () => void;
}

const brandLogos: Record<string, string> = {
  abb: abbLogo,
  "cita ev": citaLogo,
  tesla: teslaLogo,
};

const getBrandLogo = (brand: string) =>
  brandLogos[brand.trim().toLowerCase()];

const matchesCatalog = (value: string | undefined, selected: string) =>
  value?.trim().toLowerCase() === selected.trim().toLowerCase();

const getDisplayBrands = (
  catalogEntries: EVChargerCatalogItem[],
): IBrandOption[] =>
  Array.from(
    new Set(
      catalogEntries
        .map((item) => item.brand?.trim())
        .filter((brand): brand is string => Boolean(brand)),
    ),
  ).map((brand) => ({
    id: brand,
    label: brand,
    logo: getBrandLogo(brand),
  }));

const getModelsForBrand = (
  catalogEntries: EVChargerCatalogItem[],
  selectedEvBrand: string,
) => {
  if (!selectedEvBrand) {
    return [];
  }

  return Array.from(
    new Set(
      catalogEntries
        .filter((item) => matchesCatalog(item.brand, selectedEvBrand))
        .map((item) => item.modelNumber?.trim())
        .filter((model): model is string => Boolean(model)),
    ),
  );
};

const findCatalogEntry = (
  catalogEntries: EVChargerCatalogItem[],
  selectedEvBrand: string,
  selectedEvModel: string,
) =>
  catalogEntries.find(
    (item) =>
      matchesCatalog(item.brand, selectedEvBrand) &&
      matchesCatalog(item.modelNumber, selectedEvModel),
  );

const validateWizardStep = (
  wizardStep: number,
  selectedEvBrand: string,
  selectedEvModel: string,
) => {
  if (wizardStep === 0) {
    if (!selectedEvBrand) {
      return { brandError: "Please select the brand" };
    }
    return { nextStep: 1 };
  }

  if (wizardStep === 1) {
    if (!selectedEvModel) {
      return { modelError: "Please select the model" };
    }
    return { nextStep: 2 };
  }

  return {};
};

const buildUpdatePayload = (
  data: IFormInput,
  deviceToEdit: IPowerConsumerView,
  selectedEvBrand: string,
  selectedEvModel: string,
  catalogEntry?: EVChargerCatalogItem,
): UpdateEVChargerRequestDTO => ({
  evChargerId: deviceToEdit.id,
  name: data.name,
  type: "EV_CHARGER",
  category: "EV_CHARGER",
  brand: selectedEvBrand || deviceToEdit.brand,
  modelNumber: selectedEvModel || deviceToEdit.modelNumber,
  serialKey: data.serialKey || "",
  powerUsage: catalogEntry?.maxPower ?? deviceToEdit.powerUsageInWatt ?? 0,
  chargerPointId: data.serialKey || "",
  firmwareVersion: deviceToEdit.firmwareVersion ?? null,
  zoneId: deviceToEdit.zoneId ?? null,
  chargerProtocol: catalogEntry?.chargerProtocol ?? deviceToEdit.protocol ?? null,
  numberOfConnectors:
    catalogEntry?.numberOfConnectors ?? deviceToEdit.numberOfConnectors ?? null,
  maxPower: catalogEntry?.maxPower ?? deviceToEdit.maxPower ?? null,
  maxCurrent: catalogEntry?.maxCurrent ?? deviceToEdit.maxCurrent ?? null,
  supportsSmartCharging:
    catalogEntry?.supportsSmartCharging ??
    deviceToEdit.supportsSmartCharging ??
    null,
  supportsChargingProfiles:
    catalogEntry?.supportsChargingProfiles ??
    deviceToEdit.supportsChargingProfiles ??
    null,
  supportsRemoteStartStop:
    catalogEntry?.supportsRemoteStartStop ??
    deviceToEdit.supportsRemoteStartStop ??
    null,
  catalogId: catalogEntry?.catalogId ?? null,
});

const buildAddPayload = (
  data: IFormInput,
  selectedEvBrand: string,
  selectedEvModel: string,
  catalogEntry?: EVChargerCatalogItem,
): AddEvChargerRequestDTO => ({
  name: data.name,
  type: "EV_CHARGER",
  category: "EV_CHARGER",
  brand: selectedEvBrand,
  modelNumber: selectedEvModel,
  serialKey: data.serialKey || "",
  powerUsage: catalogEntry?.maxPower ?? 0,
  chargerPointId: data.serialKey || "",
  firmwareVersion: null,
  zoneId: null,
  chargerProtocol: catalogEntry?.chargerProtocol ?? null,
  numberOfConnectors: catalogEntry?.numberOfConnectors ?? null,
  maxPower: catalogEntry?.maxPower ?? null,
  maxCurrent: catalogEntry?.maxCurrent ?? null,
  supportsSmartCharging: catalogEntry?.supportsSmartCharging ?? null,
  supportsChargingProfiles: catalogEntry?.supportsChargingProfiles ?? null,
  supportsRemoteStartStop: catalogEntry?.supportsRemoteStartStop ?? null,
  catalogId: catalogEntry?.catalogId ?? null,
});

const WizardTitle = ({
  wizardStep,
  selectedEvBrand,
  isEdit,
  onBack,
}: {
  wizardStep: number;
  selectedEvBrand: string;
  isEdit: boolean;
  onBack: () => void;
}) => {
  if (wizardStep === 0) {
    return (
      <div className="d-flex gap-2 align-items-center">
        <div>
          <div className="font-size-24 font-weight-500 text-dark">
            Select Brand
          </div>
          <div className="font-size-16 font-weight-400 text-light">
            Select a compatible EV charger brand from the list below.
          </div>
        </div>
      </div>
    );
  }

  if (wizardStep === 1) {
    return (
      <div className="d-flex gap-2 align-items-center">
        <div>
          <BackArrowButton onBack={onBack} bgColor="--background" />
        </div>
        <div>
          <div className="font-size-24 font-weight-500 text-dark">
            Select Model
          </div>
          <div className="font-size-16 font-weight-400 text-light">
            Select your {selectedEvBrand} EV charger model from the list below.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex gap-2 align-items-center">
      <div>
        <BackArrowButton onBack={onBack} bgColor="--background" />
      </div>
      <div>
        <div className="font-size-24 font-weight-500 text-dark">
          {isEdit ? "Edit Device" : "Add Device"}
        </div>
        <div className="font-size-16 font-weight-400 text-light mt-1">
          {isEdit
            ? "Update EV Charger Information"
            : "Add EV Charger Information"}
        </div>
      </div>
    </div>
  );
};

const CatalogOptionList = ({
  isLoading,
  loadingText,
  emptyText,
  optionsLength,
  children,
}: {
  isLoading: boolean;
  loadingText: string;
  emptyText: string;
  optionsLength: number;
  children: JSX.Element[];
}) => {
  if (isLoading) {
    return <div className="p-3 font-size-14 text-light">{loadingText}</div>;
  }

  if (optionsLength === 0) {
    return <div className="p-3 font-size-14 text-light">{emptyText}</div>;
  }

  return <>{children}</>;
};

const SearchableOptionStep = ({
  searchName,
  searchValue,
  onSearchChange,
  errors,
  errorMessage,
  listContent,
}: {
  searchName: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  errors: FieldErrors<IFormInput>;
  errorMessage: string;
  listContent: JSX.Element;
}) => (
  <div className="mt-2">
    <AppInput
      name={searchName}
      register={undefined}
      errors={errors}
      placeholder="Search"
      icon="search"
      value={searchValue}
      onChange={(e) => onSearchChange(e.target.value)}
    />
    <div
      className={`${styles.selectBrand} ${styles.optionList} rounded-3 bg-white`}
    >
      {listContent}
    </div>
    {errorMessage ? (
      <div className="error font-size-14 mt-2">{errorMessage}</div>
    ) : null}
  </div>
);

const DeviceDetailsStep = ({
  selectedBrand,
  selectedEvBrand,
  selectedEvModel,
  register,
  errors,
}: {
  selectedBrand?: IBrandOption;
  selectedEvBrand: string;
  selectedEvModel: string;
  register: ReturnType<typeof useForm<IFormInput>>["register"];
  errors: FieldErrors<IFormInput>;
}) => (
  <div className="mt-2">
    <div className="bg-background-3 p-3 rounded-4 mb-4 d-flex align-items-center">
      <div className="d-flex align-items-center justify-content-center me-3">
        {selectedBrand?.logo ? (
          <img src={selectedBrand.logo} alt={selectedEvBrand} />
        ) : (
          <span className="font-size-14 font-weight-500 text-dark">
            {selectedEvBrand
              ? selectedEvBrand.substring(0, 2).toUpperCase()
              : "EV"}
          </span>
        )}
      </div>
      <div>
        <div className="font-size-14 text-dark font-weight-600">
          {selectedBrand?.label || selectedEvBrand || ""}
        </div>
        <div className="font-size-14 text-dark font-weight-600">
          {selectedEvModel}
        </div>
      </div>
    </div>
    <AppInput
      className="mt-4"
      label="Device Name"
      placeholder="Device Name"
      name="name"
      register={register("name", {
        required: "Device name is required",
        maxLength: {
          value: 20,
          message: "Maximum 20 characters allowed",
        },
        pattern: {
          value: noSpecialCharsNoWhitespaceStartEndAndNotAllowCommaRegex,
          message: "Invalid characters",
        },
      })}
      errors={errors}
    />
    <AppInput
      className="mt-4"
      label="Serial Number"
      placeholder="Serial Number"
      name="serialKey"
      register={register("serialKey", {
        required: "Serial Number is required",
      })}
      errors={errors}
    />
  </div>
);

const AddUpdateDeviceModal = (props: IProps) => {
  const { show, onClose, onCancel, deviceToEdit, onSuccess, ...rest } = props;

  const [wizardStep, setWizardStep] = useState(0);
  const [selectedEvBrand, setSelectedEvBrand] = useState("");
  const [selectedEvModel, setSelectedEvModel] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [brandError, setBrandError] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [modelError, setModelError] = useState("");

  const { data: catalogData, isLoading: isLoadingCatalog } =
    useGetEvChargerCatalogQuery();
  const catalogEntries = catalogData?.catalogEntries || [];

  const [addEvCharger, { isLoading: isAdding }] = useAddEvChargerMutation();
  const [updateEvCharger, { isLoading: isUpdating }] =
    useUpdateEvChargerMutation();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const clearEvData = () => {
    setSelectedEvBrand("");
    setSelectedEvModel("");
    setValue("brand", "");
    setValue("modelNumber", "");
    setBrandSearchTerm("");
    setModelSearchTerm("");
    setBrandError("");
    setModelError("");
  };

  const handleResetAndClose = () => {
    setWizardStep(0);
    clearEvData();
    reset();
    onClose?.();
    onCancel?.();
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    if (deviceToEdit) {
      setValue("name", deviceToEdit.name || "");
      setValue("serialKey", deviceToEdit.serialKey || "");
      setSelectedEvBrand(deviceToEdit.brand || "");
      setSelectedEvModel(deviceToEdit.modelNumber || "");
      setWizardStep(2);
      return;
    }

    setWizardStep(0);
    clearEvData();
    reset();
  }, [deviceToEdit, show, setValue, reset]);

  const displayBrands = getDisplayBrands(catalogEntries);
  const filteredBrands = displayBrands.filter((b) =>
    b.label.toLowerCase().includes(brandSearchTerm.toLowerCase()),
  );
  const filteredModels = getModelsForBrand(
    catalogEntries,
    selectedEvBrand,
  ).filter((m) => m.toLowerCase().includes(modelSearchTerm.toLowerCase()));
  const catalogEntry = findCatalogEntry(
    catalogEntries,
    selectedEvBrand,
    selectedEvModel,
  );
  const selectedBrand = displayBrands.find((b) => b.id === selectedEvBrand);

  const handleNextStep = () => {
    const result = validateWizardStep(
      wizardStep,
      selectedEvBrand,
      selectedEvModel,
    );
    setBrandError(result.brandError ?? "");
    setModelError(result.modelError ?? "");

    if (result.nextStep === undefined) {
      return;
    }

    if (result.nextStep === 2) {
      setValue("brand", selectedEvBrand);
      setValue("modelNumber", selectedEvModel);
    }

    setWizardStep(result.nextStep);
  };

  const handleBackStep = () => {
    if (wizardStep === 0) {
      handleResetAndClose();
      return;
    }
    setWizardStep(wizardStep - 1);
  };

  const onSubmit = (data: IFormInput) => {
    const saveRequest = deviceToEdit
      ? updateEvCharger(
          buildUpdatePayload(
            data,
            deviceToEdit,
            selectedEvBrand,
            selectedEvModel,
            catalogEntry,
          ),
        )
      : addEvCharger(
          buildAddPayload(
            data,
            selectedEvBrand,
            selectedEvModel,
            catalogEntry,
          ),
        );

    saveRequest
      .unwrap()
      .then(() => {
        showSuccessMessage(
          deviceToEdit
            ? "Device updated successfully"
            : "Device added successfully",
        );
        onSuccess?.();
        handleResetAndClose();
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err.message || "Failed to save device");
      });
  };

  const brandListContent = (
    <CatalogOptionList
      isLoading={isLoadingCatalog}
      loadingText="Loading brands..."
      emptyText="No brands found."
      optionsLength={filteredBrands.length}
    >
      {filteredBrands.map((brand) => (
        <button
          type="button"
          key={brand.id}
          onClick={() => {
            setSelectedEvBrand(brand.id);
            setSelectedEvModel("");
            setModelSearchTerm("");
            setBrandError("");
          }}
          className={`p-3 ${styles.selectBrand} w-100 text-start border-0 cursor-pointer d-flex align-items-center ${selectedEvBrand === brand.id ? "bg-icon-color" : ""}`}
        >
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={brand.label}
              style={{ height: 24, width: "auto" }}
              className="me-3"
            />
          ) : (
            <div className="me-3" style={{ width: 24 }}></div>
          )}
          <span className="font-weight-500 text-dark">{brand.label}</span>
        </button>
      ))}
    </CatalogOptionList>
  );

  const modelListContent = (
    <CatalogOptionList
      isLoading={isLoadingCatalog}
      loadingText="Loading models..."
      emptyText="No models found."
      optionsLength={filteredModels.length}
    >
      {filteredModels.map((model) => (
        <button
          type="button"
          key={model}
          onClick={() => {
            setSelectedEvModel(model);
            setModelError("");
          }}
          className={`p-3 ${styles.selectBrand} w-100 text-start border-0 cursor-pointer d-flex align-items-center ${selectedEvModel === model ? "bg-icon-color" : ""}`}
        >
          <span className="font-weight-500 text-dark">{model}</span>
        </button>
      ))}
    </CatalogOptionList>
  );

  const wizardSteps = [
    <SearchableOptionStep
      key="brand"
      searchName="brandSearch"
      searchValue={brandSearchTerm}
      onSearchChange={setBrandSearchTerm}
      errors={errors}
      errorMessage={brandError}
      listContent={brandListContent}
    />,
    <SearchableOptionStep
      key="model"
      searchName="modelSearch"
      searchValue={modelSearchTerm}
      onSearchChange={setModelSearchTerm}
      errors={errors}
      errorMessage={modelError}
      listContent={modelListContent}
    />,
    <DeviceDetailsStep
      key="details"
      selectedBrand={selectedBrand}
      selectedEvBrand={selectedEvBrand}
      selectedEvModel={selectedEvModel}
      register={register}
      errors={errors}
    />,
  ];

  return (
    <ModalContainer
      show={show}
      {...rest}
      onClose={handleResetAndClose}
      onCancel={handleResetAndClose}
      title={
        <WizardTitle
          wizardStep={wizardStep}
          selectedEvBrand={selectedEvBrand}
          isEdit={Boolean(deviceToEdit)}
          onBack={handleBackStep}
        />
      }
      size="modal-lg"
      onConfirm={
        wizardStep === 2 ? () => void handleSubmit(onSubmit)() : handleNextStep
      }
      cancelButtonText="Cancel"
      confirmButtonText={wizardStep === 2 ? "Confirm" : "Next"}
      confirmId="add-device-confirm"
      differentHeader={false}
      isLoading={isAdding || isUpdating || (wizardStep < 2 && isLoadingCatalog)}
    >
      {wizardSteps[wizardStep]}
    </ModalContainer>
  );
};

export default AddUpdateDeviceModal;
